import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import ThreeMonthCalendar from "../components/ThreeMonthCalendar";
import { useToast } from "../components/Toast";
import { Check } from "lucide-react";
import { getFeatureIcon } from "../lib/featureIcons";

const dayDiff = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));
};

export default function BookingPage() {
  const { t, pick, lang } = useLang();
  const { settings } = useSettings();
  const { push: toast } = useToast();

  const [houses, setHouses] = useState([]);
  const [features, setFeatures] = useState([]);
  const [bookedMap, setBookedMap] = useState({}); // iso -> [{tiny_house_id,...}]

  const [houseId, setHouseId] = useState("");
  const [range, setRange] = useState({ start: null, end: null });
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [txn, setTxn] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/tiny-houses").then((r) => {
      setHouses(r.data || []);
      if (r.data?.[0]) setHouseId(r.data[0].id);
    });
    api.get("/features").then((r) => setFeatures(r.data || []));
    api.get("/bookings/calendar").then((r) => setBookedMap(r.data?.booked || {}));
  }, []);

  // Booked dates for the currently selected house
  const bookedForHouse = useMemo(() => {
    if (!houseId) return [];
    return Object.entries(bookedMap)
      .filter(([, arr]) => arr.some((b) => b.tiny_house_id === houseId))
      .map(([d]) => d);
  }, [bookedMap, houseId]);

  const handlePick = (iso) => {
    if (!range.start || (range.start && range.end)) {
      setRange({ start: iso, end: null });
    } else if (iso < range.start) {
      setRange({ start: iso, end: null });
    } else if (iso === range.start) {
      setRange({ start: null, end: null });
    } else {
      // Ensure no booked date in between
      const d0 = new Date(range.start);
      const d1 = new Date(iso);
      const bad = bookedForHouse.some((b) => {
        const db = new Date(b);
        return db > d0 && db <= d1;
      });
      if (bad) {
        toast(lang === "mn" ? "Сонгосон хугацаанд захиалагдсан өдөр байна" : "Selected range overlaps a booked date", "error");
        return;
      }
      setRange({ start: range.start, end: iso });
    }
  };

  const toggleFeature = (fid) =>
    setSelectedFeatures((s) => (s.includes(fid) ? s.filter((x) => x !== fid) : [...s, fid]));

  const nights = dayDiff(range.start, range.end);
  const selectedHouse = houses.find((h) => h.id === houseId);
  const totalAddons = features
    .filter((f) => selectedFeatures.includes(f.id))
    .reduce((s, f) => s + (f.price || 0), 0);
  const total = nights * (selectedHouse?.price_per_night || 0) + totalAddons;

  const canSubmit =
    houseId && range.start && range.end && name.trim() && phone.trim() && txn.trim() && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: name.trim(),
        phone: phone.trim(),
        transaction_text: txn.trim(),
        tiny_house_id: houseId,
        feature_ids: selectedFeatures,
        check_in: range.start,
        check_out: range.end,
        note,
      });
      toast(t("booking_success"));
      setRange({ start: null, end: null });
      setSelectedFeatures([]);
      setName("");
      setPhone("");
      setTxn("");
      setNote("");
      // refresh calendar
      const r = await api.get("/bookings/calendar");
      setBookedMap(r.data?.booked || {});
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Error", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-28 md:pt-32 pb-20 px-5 md:px-10 max-w-7xl mx-auto" data-testid="booking-page">
      <div className="mb-12">
        <span className="text-xs tracking-[0.3em] uppercase opacity-60 block mb-3">{t("nav_booking")}</span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">{t("booking_title")}</h1>
        <p className="mt-4 max-w-2xl opacity-80">{t("booking_sub")}</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Left: calendar */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div>
              <label className="block text-xs tracking-widest uppercase opacity-60 mb-2">
                {t("choose_house")}
              </label>
              <div className="grid grid-cols-1 gap-3">
                {houses.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => {
                      setHouseId(h.id);
                      setRange({ start: null, end: null });
                    }}
                    className={`feature-card ${houseId === h.id ? "active" : ""}`}
                    data-testid={`house-option-${h.id}`}
                  >
                    <div className="flex items-center justify-between w-full gap-3">
                      <div className="text-left">
                        <div className="font-serif text-xl leading-tight">{pick(h, "name")}</div>
                        <div className="text-xs opacity-60 mt-0.5">
                          {h.capacity} {t("capacity")} · {h.price_per_night?.toLocaleString()} ₮ {t("per_night")}
                        </div>
                      </div>
                      {houseId === h.id && <Check size={18} />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <ThreeMonthCalendar
              bookedDates={bookedForHouse}
              selectedRange={range}
              onPick={handlePick}
            />
          </div>
        </div>

        {/* Right: form */}
        <form className="lg:col-span-7 space-y-10" onSubmit={submit} data-testid="booking-form">
          {/* Dates summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="card p-4">
              <div className="text-xs tracking-widest uppercase opacity-60">{t("check_in")}</div>
              <div className="font-serif text-2xl mt-1" data-testid="summary-check-in">{range.start || "—"}</div>
            </div>
            <div className="card p-4">
              <div className="text-xs tracking-widest uppercase opacity-60">{t("check_out")}</div>
              <div className="font-serif text-2xl mt-1" data-testid="summary-check-out">{range.end || "—"}</div>
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <label className="block text-xs tracking-widest uppercase opacity-60">{t("addons")}</label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="addons-list">
              {features.map((f) => {
                const Icon = getFeatureIcon(f.icon);
                const active = selectedFeatures.includes(f.id);
                return (
                  <button
                    type="button"
                    key={f.id}
                    onClick={() => toggleFeature(f.id)}
                    className={`feature-card ${active ? "active" : ""}`}
                    data-testid={`addon-${f.icon}`}
                  >
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: active ? "var(--primary)" : "var(--secondary)", color: active ? "var(--primary-fg)" : "var(--fg)" }}
                    >
                      <Icon size={16} />
                    </span>
                    <div className="font-serif text-lg leading-tight">{pick(f, "name")}</div>
                    {f.price > 0 && <div className="text-xs opacity-60">+{f.price.toLocaleString()} ₮</div>}
                    {f.price === 0 && <div className="text-xs opacity-60">{lang === "mn" ? "Үнэгүй" : "Free"}</div>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact fields */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("your_name")}</label>
              <input
                className="input-line"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="input-name"
                required
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("your_phone")}</label>
              <input
                className="input-line"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="input-phone"
                required
              />
            </div>
          </div>

          {/* Payment */}
          <div className="card p-6 md:p-8" data-testid="payment-card">
            <h3 className="font-serif text-2xl mb-1">{t("payment_title")}</h3>
            <p className="opacity-80 text-sm mb-5">{t("payment_desc")}</p>
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div>
                {settings.socialpay_qr_base64 ? (
                  <img
                    src={`data:image/png;base64,${settings.socialpay_qr_base64}`}
                    alt="SocialPay QR"
                    className="w-full max-w-[260px] rounded-2xl border border-[color:var(--border)]"
                    data-testid="socialpay-qr"
                  />
                ) : (
                  <div
                    className="w-full max-w-[260px] aspect-square rounded-2xl flex items-center justify-center text-center text-sm opacity-70 p-6"
                    style={{ background: "var(--muted)" }}
                    data-testid="socialpay-qr-placeholder"
                  >
                    {lang === "mn" ? "Админ SocialPay QR-ыг оруулаагүй байна" : "Admin has not uploaded SocialPay QR yet"}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">
                    {t("transaction_text")}
                  </label>
                  <input
                    className="input-line"
                    placeholder={lang === "mn" ? "Жишээ нь: БАТБАЯР 031" : "e.g., BATBAYAR 031"}
                    value={txn}
                    onChange={(e) => setTxn(e.target.value)}
                    data-testid="input-transaction"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase opacity-60 mb-1">{t("note")}</label>
                  <textarea
                    className="input-box"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="input-note"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Total + submit */}
          <div className="card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="booking-total">
            <div>
              <div className="text-xs tracking-widest uppercase opacity-60">{t("total")}</div>
              <div className="font-serif text-3xl mt-1">
                {total.toLocaleString()} ₮
              </div>
              <div className="text-xs opacity-60 mt-1">
                {nights} {t("nights")} {selectedHouse ? `· ${pick(selectedHouse, "name")}` : ""}
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSubmit}
              data-testid="submit-booking-btn"
              style={{ opacity: canSubmit ? 1 : 0.5 }}
            >
              {t("submit_booking")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
